/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState, useRef, type FC } from "react";
import { useTranslation } from "react-i18next";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";
import { cn, inputSurface } from "@design-system";
import "./LgbDatepicker.css";
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from "@floating-ui/dom";

export interface LGBDatepickerProp {
  inputId?: string;
  selectedDate?: Date;
  showError?: boolean;
  select: (selectedDate: Date) => void;
  allowPastDates?: boolean;
  minDate?: Date;
  /** null removes the default one-year upper limit. */
  maxDate?: Date | null;
  /** Float above scrollable dialogs without affecting their layout. */
  overlayCalendar?: boolean;
}

export const LGBDatepicker: FC<LGBDatepickerProp> = (props) => {
  const { t } = useTranslation("common");
  const [value, setValue] = useState<DateValueType>({
    startDate: null,
    endDate: null,
  });

  const rawLocale = t("locale"); // e.g., "en-US", "nb-no"
  // Ensure Day.js compatible locale (e.g., "en", "nb") is passed to the datepicker
  // Default to "en" if parsing fails or locale is undefined
  const datepickerLocale = rawLocale?.split("-")[0].toLowerCase() || "en";

  // Bruk en ref for å spore om vi allerede har oppdatert fra props
  const isInitializedRef = useRef(false);
  const datepickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Hvis selectedDate er definert og vi ikke allerede har initialisert
    if (props.selectedDate && !isInitializedRef.current) {
      setValue({
        startDate: props.selectedDate,
        endDate: props.selectedDate,
      });
      isInitializedRef.current = true;
    } else if (props.selectedDate) {
      // Sammenlign datoer for å unngå unødvendige oppdateringer
      const currentStartDate =
        value?.startDate instanceof Date ? value.startDate.getTime() : null;
      const newStartDate =
        props.selectedDate instanceof Date
          ? props.selectedDate.getTime()
          : null;

      if (currentStartDate !== newStartDate && newStartDate !== null) {
        setValue({
          startDate: props.selectedDate,
          endDate: props.selectedDate,
        });
      }
    }
  }, [props.selectedDate]);

  const handleValueChange = (newValue: DateValueType) => {
    setValue(newValue);
    if (newValue?.startDate) {
      switch (typeof newValue.startDate) {
        case "string":
          props.select(new Date(`${newValue.startDate}T00:00:00`));
          break;
        case "object":
          props.select(newValue.startDate as Date);
          break;
      }
    }
  };

  // Datepicker 1.x exposes visibility through classes rather than a controlled
  // open prop. Keep this adapter scoped to dialog calendars; the top layer
  // avoids clipping by scroll containers while preserving the dialog DOM tree.
  useEffect(() => {
    if (!props.overlayCalendar) return;
    const root = datepickerRef.current;
    const input = root?.querySelector("input");
    const popup = root?.querySelector<
      HTMLElement & { showPopover?: () => void; hidePopover?: () => void }
    >(".lgb-datepicker-overlay > div");
    if (!root || !input || !popup) return;
    popup.setAttribute("popover", "manual");
    let cleanupPosition: (() => void) | undefined;
    let isOpen = false;
    const position = () => {
      void computePosition(input, popup, {
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [offset(6), flip(), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        if (isOpen)
          Object.assign(popup.style, { left: `${x}px`, top: `${y}px` });
      });
    };
    const sync = () => {
      const nextOpen = popup.classList.contains("block");
      // The library adds `hidden` after a 300 ms animation. A quick reopen
      // must not be hidden by that stale timeout.
      if (nextOpen && popup.classList.contains("hidden"))
        popup.classList.remove("hidden");
      if (nextOpen === isOpen) return;
      isOpen = nextOpen;
      input.setAttribute("aria-expanded", String(isOpen));
      if (isOpen) {
        popup.showPopover?.();
        cleanupPosition = autoUpdate(input, popup, position);
      } else {
        popup.classList.add("hidden");
        cleanupPosition?.();
        cleanupPosition = undefined;
        popup.hidePopover?.();
        if (document.activeElement === input) input.blur();
      }
    };
    const close = () => {
      popup.classList.remove("block");
      popup.classList.add("hidden");
      sync();
    };
    const toggleInput = (event: PointerEvent) => {
      if (event.target === input && isOpen) {
        event.preventDefault();
        close();
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    };
    input.setAttribute("aria-expanded", "false");
    const observer = new MutationObserver(sync);
    observer.observe(popup, { attributes: true, attributeFilter: ["class"] });
    root.addEventListener("pointerdown", toggleInput, true);
    document.addEventListener("keydown", escape, true);
    return () => {
      isOpen = false;
      observer.disconnect();
      cleanupPosition?.();
      popup.hidePopover?.();
      root.removeEventListener("pointerdown", toggleInput, true);
      document.removeEventListener("keydown", escape, true);
    };
  }, [props.overlayCalendar]);

  const maxDate = new Date();
  // Sett minDate til null hvis allowPastDates er true, ellers bruk dagens dato
  const minDate =
    props.minDate ?? (props.allowPastDates ? undefined : new Date());
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const errorStyle =
    "border-lgb-red-200 bg-red-50 text-red-900 placeholder-red-700 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:bg-danger-muted dark:focus:border-red-500 dark:focus:ring-red-500";

  // Legg til en stil for å sikre at datepicker-popupen vises over alle andre elementer
  useEffect(() => {
    // Legg til en CSS-regel for å sikre at datepicker-popupen vises over alle andre elementer
    const style = document.createElement("style");
    style.innerHTML = `
      /* Sikre at datepicker-popupen vises over alle andre elementer */
      .react-tailwindcss-datepicker-container {
        position: fixed !important;
        z-index: 9999 !important;
      }
      
      /* Fjern overflow: hidden fra kartets liste-overlay slik at datepickeren
         ikke kuttes. Selektorene matcher design-system-klassene (mapOverlay /
         mapOverlayToolbar) og overlay-wrapperen på kartsidene. */
      .absolute.inset-4.w-\\[280px\\].z-10,
      .bg-surface.border.border-border.rounded-lg.shadow-sm.h-full.flex.flex-col.overflow-hidden,
      .p-2.flex-shrink-0.border-b.border-border {
        overflow: visible !important;
      }
      
      /* Endre fargen på valgt dato til lgb-blue-300 */
      .bg-indigo-500, 
      .bg-indigo-600,
      button.bg-indigo-500, 
      button.bg-indigo-600 {
        background-color: #7214FF !important; /* lgb-blue-300 */
      }
      
      /* Sikre at teksten på valgt dato er synlig */
      .bg-indigo-500 span, 
      .bg-indigo-600 span,
      button.bg-indigo-500 span, 
      button.bg-indigo-600 span {
        color: white !important;
      }
      
      /* Endre fargen på dagens dato (border) */
      .border-indigo-500,
      .border-indigo-600,
      button.border-indigo-500,
      button.border-indigo-600,
      .today {
        border-color: #7214FF !important; /* lgb-blue-300 */
      }
      
      /* Endre fargen på hover-effekten */
      .hover\\:bg-indigo-100:hover,
      button.hover\\:bg-indigo-100:hover {
        background-color: rgba(114, 20, 255, 0.1) !important; /* lgb-blue-300 med opacity */
      }
      
      /* Legg til cursor: pointer på hover for date input */
      .react-tailwindcss-datepicker-input-container input {
        cursor: pointer !important;
        padding-right: 40px !important; /* Gi plass til kalender-ikonet */
      }

      /* Skjul clear-knappen */
      .react-tailwindcss-datepicker-input-container button {
        display: none !important;
      }
      
      /* Skjul SVG-ikoner fra datepicker */
      .react-tailwindcss-datepicker-input-container svg {
        display: none !important;
      }
      
      /* Stil for kalender-ikonet */
      .calendar-icon {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        color: #6B7280;
      }
      
      .dark .calendar-icon {
        color: #9CA3AF;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="relative" ref={datepickerRef}>
      <Datepicker
        inputId={props.inputId}
        popoverDirection={props.overlayCalendar ? "down" : undefined}
        startFrom={props.overlayCalendar ? props.selectedDate : undefined}
        inputClassName={cn(
          props.showError && !props.selectedDate ? errorStyle : "",
          inputSurface,
          "relative w-full cursor-pointer",
        )}
        i18n={datepickerLocale}
        minDate={minDate}
        maxDate={props.maxDate === undefined ? maxDate : props.maxDate}
        displayFormat={"DD.MM.YYYY"}
        asSingle
        startWeekOn="mon"
        useRange={false}
        value={value}
        onChange={(e) => handleValueChange(e)}
        primaryColor={"indigo"}
        containerClassName={cn(
          "lgb-datepicker-container",
          props.overlayCalendar && "lgb-datepicker-overlay",
        )}
        toggleClassName="hidden"
        readOnly={true}
      />
      {/* Kalender-ikon */}
      <div className="calendar-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
          />
        </svg>
      </div>
      {props.showError && !props.selectedDate && (
        <span className="absolute left-0 -bottom-6 text-sm text-red-600 dark:text-red-500">
          {t("validation:route_info.date")}
        </span>
      )}
    </div>
  );
};
